import React, { useState } from "react";
import { ImageIcon, Check } from "lucide-react";

/**
 * Branded virtual-background picker for video meetings. Calls Jitsi's
 * `setVirtualBackground` ExternalAPI command so every participant sees the
 * host's chosen branded backdrop behind them. Three society-themed presets:
 *   • Classic — deep navy + Dhamma chakra in gold (default formal look)
 *   • Saffron — warm festival-toned background with chakra centre-stage
 *   • Dhamma — pearl/cream, more meditative, navy chakra outline
 * Plus standard "Blur" and "None" options.
 *
 * Background images live in `/backgrounds/` (served from frontend public
 * folder). Generated by `/app/scripts/gen_meeting_backgrounds.py`.
 */

const PRESETS = [
  {
    id: "abs-classic",
    label: "Classic",
    description: "Navy + gold chakra · formal",
    url: "/backgrounds/abs-classic.jpg",
    thumb: "/backgrounds/abs-classic.jpg",
  },
  {
    id: "abs-saffron",
    label: "Saffron",
    description: "Festival warmth · chakra centred",
    url: "/backgrounds/abs-saffron.jpg",
    thumb: "/backgrounds/abs-saffron.jpg",
  },
  {
    id: "abs-dhamma",
    label: "Dhamma",
    description: "Pearl + meditative · soft tones",
    url: "/backgrounds/abs-dhamma.jpg",
    thumb: "/backgrounds/abs-dhamma.jpg",
  },
];

export default function VirtualBackgroundPicker({ apiRef }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  const apply = (preset) => {
    try {
      const url = `${window.location.origin}${preset.url}`;
      apiRef?.current?.executeCommand("setVirtualBackground", {
        enabled: true,
        backgroundType: "image",
        selectedThumbnail: preset.id,
        url,
      });
      setActive(preset.id);
    } catch (e) { /* ignore */ }
    setOpen(false);
  };

  const applyBlur = () => {
    try {
      apiRef?.current?.executeCommand("setVirtualBackground", {
        enabled: true,
        backgroundType: "blur",
        blurValue: 25,
        selectedThumbnail: "blur",
      });
      setActive("blur");
    } catch (e) { /* ignore */ }
    setOpen(false);
  };

  const clearBg = () => {
    try {
      apiRef?.current?.executeCommand("setVirtualBackground", { enabled: false });
      setActive(null);
    } catch (e) { /* ignore */ }
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="meeting-bg-picker-button"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white/5 border border-white/15 text-pearl/80 hover:border-saffron hover:text-saffron"
        title="Choose a branded virtual background"
      >
        <ImageIcon size={13} />
        Background
      </button>
      {open && (
        <div
          data-testid="meeting-bg-picker"
          className="absolute right-0 top-full mt-2 w-[280px] bg-[#0B1F4D] border border-white/15 rounded-lg shadow-2xl p-2 z-50"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-saffron px-2 pb-1 pt-1">
            Society backgrounds
          </div>
          <div className="grid grid-cols-3 gap-1.5 p-1">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => apply(p)}
                data-testid={`meeting-bg-${p.id}`}
                title={`${p.label} — ${p.description}`}
                className={`relative aspect-video rounded overflow-hidden border-2 transition-colors ${
                  active === p.id ? "border-saffron" : "border-white/15 hover:border-saffron/60"
                }`}
              >
                <img src={p.thumb} alt={p.label} className="w-full h-full object-cover" />
                {active === p.id && (
                  <div className="absolute inset-0 bg-saffron/30 flex items-center justify-center">
                    <Check size={16} className="text-pearl drop-shadow" />
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-black/55 text-pearl text-[9px] py-0.5 text-center">
                  {p.label}
                </div>
              </button>
            ))}
          </div>
          <div className="h-px bg-white/10 my-1" />
          <div className="grid grid-cols-2 gap-1.5 p-1">
            <button
              onClick={applyBlur}
              data-testid="meeting-bg-blur"
              className={`px-2 py-1.5 rounded text-[11px] border transition-colors ${
                active === "blur"
                  ? "bg-saffron/20 border-saffron text-saffron"
                  : "border-white/15 text-pearl/80 hover:border-saffron hover:text-saffron"
              }`}
            >
              Blur
            </button>
            <button
              onClick={clearBg}
              data-testid="meeting-bg-none"
              className="px-2 py-1.5 rounded text-[11px] border border-white/15 text-pearl/80 hover:border-saffron hover:text-saffron"
            >
              None
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
